import { useState } from 'react';
import { useBlocker } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { SaveChangesButton } from '@/components/glitch-ui/save-changes-button';

interface UseUnsavedChangesWarningProps {
  isDirty: boolean;
  onSave: () => Promise<void>;
  onDiscard?: () => void;
  isSaving?: boolean;
  /** Base path to allow navigation within (e.g., '/campaigns/abc123'). Navigation within this path won't trigger the warning. */
  allowedBasePath?: string;
  /** Name to display in the warning message (e.g., campaign name) */
  contextName?: string;
  /** If set, Save is blocked and this message is shown instead. Changes can only be discarded. */
  saveBlockedReason?: string | null;
}

/**
 * Hook that shows a warning modal when user tries to navigate away with unsaved changes.
 * Returns a component that must be rendered in the page.
 */
export function useUnsavedChangesWarning({
  isDirty,
  onSave,
  onDiscard,
  isSaving = false,
  allowedBasePath,
  contextName,
  saveBlockedReason,
}: UseUnsavedChangesWarningProps) {
  const [saveError, setSaveError] = useState<string | null>(null);

  // True while a successful save → proceed() is in flight. Forces the dialog
  // closed even if ``blocker.status`` briefly returns to ``'blocked'`` during
  // the navigation transition. Without this, the dialog "double-flashes"
  // because Radix Presence catches the open prop toggling back on mid-fade
  // (visible as the backdrop dimming → revealing the page → dimming again).
  // Has to be state, not a ref — the AlertDialog's ``open`` prop needs to
  // re-evaluate when it changes. #4359.
  const [isProceedingAfterSave, setIsProceedingAfterSave] = useState(false);

  const blocker = useBlocker({
    shouldBlockFn: ({ next }) => {
      if (isProceedingAfterSave) return false;
      if (!isDirty) return false;
      // Allow navigation within the same base path (e.g., within the same campaign).
      // Match on a path-segment boundary (exact, or base + '/') rather than a loose
      // string prefix, so sibling paths that merely share a prefix still prompt —
      // e.g. when the base is `.../ad/12`, switching to `.../ad/120` must NOT be
      // treated as "same place" (that would skip the prompt and silently drop the
      // edit, since the ad route remounts per id). #5598.
      if (allowedBasePath && (next.pathname === allowedBasePath || next.pathname.startsWith(`${allowedBasePath}/`))) {
        return false;
      }
      setSaveError(null);
      return true;
    },
    enableBeforeUnload: () => isDirty,
    withResolver: true,
  });

  // Derived "should the dialog be visible?" — gates on both the blocker's own
  // status AND our local proceeding guard, so the dialog can never re-open
  // mid-proceed even if blocker.status transitions through 'blocked' again.
  const isModalOpen = blocker.status === 'blocked' && !isProceedingAfterSave;

  const handleSaveAndContinue = async () => {
    try {
      await onSave();
      setSaveError(null);
      // Flip the guard BEFORE proceed() so the dialog's open prop re-evaluates
      // to false on the very next render. Source route then unmounts as
      // navigation completes, which also tears down this hook instance.
      setIsProceedingAfterSave(true);
      blocker.proceed?.();
      // Safety reset on the next tick in case proceed() bails (e.g. router
      // intercepts) and the hook stays mounted — the next user edit cycle
      // should still get a fresh warning.
      window.setTimeout(() => setIsProceedingAfterSave(false), 0);
    } catch (error) {
      // Save failed — show error, disable Save, user can only Discard or Cancel
      setSaveError(error instanceof Error ? error.message : 'Failed to save changes');
    }
  };

  const handleDiscardAndContinue = () => {
    setSaveError(null);
    blocker.proceed?.();
    onDiscard?.();
  };

  const handleCancel = () => {
    setSaveError(null);
    blocker.reset?.();
  };

  const isBlocked = !!saveBlockedReason || !!saveError;

  // Return JSX (an element), not a component function. Returning a function
  // means every parent re-render produces a NEW function reference; React
  // sees a different ``element.type`` and unmounts → remounts the AlertDialog,
  // which makes Radix Presence cycle its open/close animation. During the
  // many re-renders triggered by ``await onSave()`` (mutations + refetches),
  // those cycles compound into a visible flicker. Discard doesn't show the
  // issue because it's synchronous (only 1-2 re-renders). #4359.
  const UnsavedChangesModal = (
    <AlertDialog open={isModalOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
          <AlertDialogDescription>
            {isBlocked
              ? 'Your changes cannot be saved and will be discarded.'
              : `You have unsaved changes${contextName ? ` in ${contextName}` : ''}. Would you like to save them before leaving?`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {isBlocked && (
          <div className="flex items-center gap-2 text-sm text-[#B91C1C] px-1">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{saveBlockedReason || saveError}</span>
          </div>
        )}
        <AlertDialogFooter className="flex gap-2 sm:justify-end">
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant={isBlocked ? 'default' : 'outline'}
            onClick={handleDiscardAndContinue}
            disabled={isSaving}
            className={isBlocked ? 'bg-[#FEE2E2] hover:bg-[#FDD6D6] text-[#B91C1C] border border-[#FCA5A5] shadow-none' : ''}
          >
            Discard Changes
          </Button>
          {!isBlocked && <SaveChangesButton onClick={handleSaveAndContinue} saving={isSaving} />}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { UnsavedChangesModal };
}
